import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { apiRequest } from '../../services/api';

const INP = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-700 focus:outline-none focus:border-blue-400 bg-white transition-all placeholder-gray-300";
const LABEL = "text-xs font-bold text-gray-500 uppercase tracking-widest block mb-2";

const VARSAYILAN = {
  siteBasligi: '',
  metaAciklama: '',
  anahtarKelimeler: '',
  ogResimUrl: '',
  googleAnalyticsId: '',
  facebookPixelId: '',
  schemaAktif: true,
  sitemapAktif: true,
  robotsDirektifi: 'index, follow',
};

export default function WebSiteSEO() {
  const [yukleniyor, setYukleniyor] = useState(true);
  const [form, setForm] = useState(VARSAYILAN);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [mesaj, setMesaj] = useState('');

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const data = await apiRequest('website');
      if (data?.seoAyar) {
        const s = data.seoAyar;
        setForm({
          siteBasligi: s.siteBasligi || '',
          metaAciklama: s.metaAciklama || '',
          anahtarKelimeler: s.anahtarKelimeler || '',
          ogResimUrl: s.ogResimUrl || '',
          googleAnalyticsId: s.googleAnalyticsId || '',
          facebookPixelId: s.facebookPixelId || '',
          schemaAktif: s.schemaAktif ?? true,
          sitemapAktif: s.sitemapAktif ?? true,
          robotsDirektifi: s.robotsDirektifi || 'index, follow',
        });
      }
    } catch { }
    setYukleniyor(false);
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function kaydet() {
    setKaydediliyor(true);
    try {
      await apiRequest('website/seo', 'PUT', form);
      setMesaj('✅ SEO ayarları kaydedildi.');
    } catch (e) {
      setMesaj('❌ ' + (e.message || 'Kaydedilemedi.'));
    } finally {
      setKaydediliyor(false);
      setTimeout(() => setMesaj(''), 3000);
    }
  }

  if (yukleniyor) return <LoadingSpinner fullPage text="SEO ayarları yükleniyor..." />;

  const baslikUzunluk = form.siteBasligi?.length || 0;
  const aciklamaUzunluk = form.metaAciklama?.length || 0;

  return (
    <div className="p-4 md:p-6 space-y-6 min-h-screen bg-gray-50">
      <PageHeader icon="🔍" iconColor="from-emerald-600 to-teal-700" iconShadow="shadow-emerald-200"
        title="Web Sitesi — SEO"
        subtitle="Arama motoru optimizasyonu ve analitik ayarları"
      >
        <button onClick={kaydet} disabled={kaydediliyor}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-6 py-2 rounded-xl text-sm font-bold transition-all">
          {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </PageHeader>

      {mesaj && (
        <div className={`p-4 rounded-xl text-sm font-semibold ${mesaj.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {mesaj}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide border-b border-gray-100 pb-3">Meta Bilgiler</h3>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={LABEL.replace('block mb-2', '')}>Site Başlığı</label>
                <span className={`text-xs font-bold ${baslikUzunluk > 60 ? 'text-red-500' : 'text-gray-400'}`}>{baslikUzunluk}/60</span>
              </div>
              <input className={INP} value={form.siteBasligi} placeholder="Otel Adı — Slogan"
                onChange={e => set('siteBasligi', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Google arama sonuçlarında görünen başlık. Maks. 60 karakter önerilir.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={LABEL.replace('block mb-2', '')}>Meta Açıklama</label>
                <span className={`text-xs font-bold ${aciklamaUzunluk > 160 ? 'text-red-500' : 'text-gray-400'}`}>{aciklamaUzunluk}/160</span>
              </div>
              <textarea className={`${INP} resize-none`} rows={3} value={form.metaAciklama}
                placeholder="Otelinizin kısa açıklaması — arama sonuçlarında görünür."
                onChange={e => set('metaAciklama', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Maks. 160 karakter önerilir.</p>
            </div>
            <div>
              <label className={LABEL}>Anahtar Kelimeler</label>
              <input className={INP} value={form.anahtarKelimeler}
                placeholder="otel, konaklama, tatil, deniz manzaralı..."
                onChange={e => set('anahtarKelimeler', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Virgülle ayırın.</p>
            </div>
            <div>
              <label className={LABEL}>Open Graph Görseli (Sosyal Medya Paylaşım)</label>
              <input className={INP} value={form.ogResimUrl}
                placeholder="https://oteliniz.com/og-image.jpg"
                onChange={e => set('ogResimUrl', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Otelinizi sosyal medyada paylaşırken görünen görsel. 1200×630 px önerilir.</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide border-b border-gray-100 pb-3">Teknik SEO</h3>
            <div>
              <label className={LABEL}>Robots Direktifi</label>
              <select className={INP} value={form.robotsDirektifi} onChange={e => set('robotsDirektifi', e.target.value)}>
                <option value="index, follow">index, follow (Önerilen)</option>
                <option value="noindex, follow">noindex, follow</option>
                <option value="index, nofollow">index, nofollow</option>
                <option value="noindex, nofollow">noindex, nofollow</option>
              </select>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <div onClick={() => set('schemaAktif', !form.schemaAktif)}
                className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${form.schemaAktif ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.schemaAktif ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">JSON-LD Yapısal Veri</p>
                <p className="text-xs text-gray-400">Google'ın oteli daha iyi anlamasına yardımcı olur.</p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <div onClick={() => set('sitemapAktif', !form.sitemapAktif)}
                className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${form.sitemapAktif ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.sitemapAktif ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Otomatik Sitemap</p>
                <p className="text-xs text-gray-400">sitemap.xml sayfaları arama motorlarına bildirir.</p>
              </div>
            </label>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide border-b border-gray-100 pb-3">Analitik & Takip</h3>
            <div>
              <label className={LABEL}>Google Analytics ID</label>
              <input className={INP} value={form.googleAnalyticsId} placeholder="G-XXXXXXXXXX"
                onChange={e => set('googleAnalyticsId', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Google Analytics 4 ölçüm kimliği.</p>
            </div>
            <div>
              <label className={LABEL}>Facebook Pixel ID</label>
              <input className={INP} value={form.facebookPixelId} placeholder="123456789012345"
                onChange={e => set('facebookPixelId', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Meta (Facebook) Pixel reklam takip kodu.</p>
            </div>
          </div>

          {/* Google Önizleme */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide border-b border-gray-100 pb-3 mb-4">Google Önizleme</h3>
            <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-1">
              <p className="text-xs text-green-700 font-mono">https://oteliniz.com/</p>
              <p className="text-blue-700 text-base font-medium leading-tight">
                {form.siteBasligi || 'Site Başlığı'}
              </p>
              <p className="text-sm text-gray-500 leading-relaxed">
                {form.metaAciklama || 'Meta açıklama burada görünecek. Arama motorlarında otelinizin açıklamasını ekleyin.'}
              </p>
            </div>
            <p className="text-xs text-gray-400 mt-3">Gerçek Google sonucu bu şekilde görünecektir.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
